import { Injectable, ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { AuthService } from './auth.service';
import { CallCenterDatabaseTableData, FilterData, StatusOption } from '../interfaces/call-center-database.interface';

/**
 * CallCenterStateService
 * 
 * Egységes állapot kezelés a Call Center táblázathoz.
 * Felelősség: DataSource, szűrés, cache, pagination, selection kezelése.
 */
@Injectable({
  providedIn: 'root'
})
export class CallCenterStateService {
  
  // === DATA SOURCE & SELECTION ===
  dataSource = new MatTableDataSource<CallCenterDatabaseTableData>([]);
  selection = new SelectionModel<CallCenterDatabaseTableData>(true, []);
  
  // === CACHE MANAGEMENT ===
  private statusClassCache = new Map<string, string>();
  private _paginatedDataCache: CallCenterDatabaseTableData[] = [];
  private _lastPaginatorState: { pageIndex: number, pageSize: number, filteredDataLength: number } | null = null;
  
  // === PAGINATION STATE ===
  private currentPageIndex: number = 0;
  private preservePaginatorState: boolean = false;
  private _serverSidePagination: boolean = false;

  // === FILTER STATE ===
  searchTerm: string = '';
  statusFilter: string[] = [];
  dateFromFilter: Date | null = null;
  dateToFilter: Date | null = null;
  assigneeFilter: string[] = [];

  // === FILTER OPTIONS ===
  statusOptions: StatusOption[] = [
    { value: 'all', label: 'Összes státusz' },
    { value: 'hivando', label: 'Hívandó' },
    { value: 'visszahivando', label: 'Visszahívandó' },
    { value: 'elutasitott', label: 'Elutasított' },
    { value: 'robinson_listas', label: 'Robinson listás' },
    { value: 'nem_vette_fel', label: 'Nem vette fel' },
    { value: 'egyeztetett-kum', label: 'Egyeztetett (KÜM)' },
    { value: 'egyeztetett-vagyon', label: 'Egyeztetett (vagyon)' },
    { value: 'egyeztetett-kv', label: 'Egyeztetett (K+v)' }
  ];

  constructor(private authService: AuthService) {}

  // === DATA SOURCE METHODS ===

  /**
   * DataSource inicializálása adatokkal és filter predicate-tel (kliens oldali szűrés)
   */
  initializeDataSource(data: CallCenterDatabaseTableData[]): void {
    this._serverSidePagination = false;
    this.dataSource = new MatTableDataSource(data);
    this.dataSource.filterPredicate = this.createFilterPredicate();
    this.initializeChipStatuses(data);
    
    // Cache törlése mivel új DataSource jön létre
    this._lastPaginatorState = null;
    this._paginatedDataCache = [];
  }

  /**
   * DataSource inicializálása szerver oldali lapozáshoz (nincs filterPredicate)
   * Az adatok már szűrve és lapozva érkeznek a backendtől
   */
  initializeDataSourceServerSide(data: CallCenterDatabaseTableData[]): void {
    this._serverSidePagination = true;
    this.dataSource = new MatTableDataSource(data);
    this.initializeChipStatuses(data);
    
    // Cache törlése
    this._lastPaginatorState = null;
    this._paginatedDataCache = [];
  }

  /**
   * Csak az adatok frissítése a DataSource-ban paginator érintése nélkül.
   * Szerver oldali lapozásnál használjuk, hogy ne írja felül a totalRecords értéket.
   */
  updateDataOnly(data: CallCenterDatabaseTableData[]): void {
    this.dataSource.data = [...data];
    this.initializeChipStatuses(data);
    
    // Cache törlése mivel új adatok érkeztek
    this._lastPaginatorState = null;
    this._paginatedDataCache = [];
  }

  /**
   * DataSource frissítése pagination állapot megőrzéssel
   */
  updateDataSourceWithPagination(data: CallCenterDatabaseTableData[], paginator: MatPaginator | null): void {
    const savedPageIndex = paginator ? paginator.pageIndex : 0;
    const savedPageSize = paginator ? paginator.pageSize : 25;
    
    this.dataSource.data = [...data];
    this.initializeChipStatuses(data);
    
    // Cache törlése mivel új adatok érkeztek
    this._lastPaginatorState = null;
    this._paginatedDataCache = [];
    
    if (paginator) {
      if (this._serverSidePagination) {
        // Szerver oldali lapozásnál NEM rendeljük a paginátort a dataSource-hoz,
        // mert az felülírná a paginator.length értékét a dataSource.data.length-szel
        // (pl. 10-re a valódi 14000 helyett). A paginator length-et a template [length]="totalRecords" kezeli.
        paginator.pageIndex = savedPageIndex;
        paginator.pageSize = savedPageSize;
      } else {
        setTimeout(() => {
          this.dataSource.paginator = null;
          paginator.pageIndex = savedPageIndex;
          paginator.pageSize = savedPageSize;
          this.dataSource.paginator = paginator;
        }, 0);
      }
    }
  }

  // === PAGINATION METHODS ===

  /**
   * Lapozott adatok lekérése cache-eléssel
   */
  getPaginatedData(paginator: MatPaginator | null, hidePaginator: boolean): CallCenterDatabaseTableData[] {
    if (hidePaginator || !paginator) {
      return this.dataSource.filteredData;
    }

    // Szerver oldali lapozásnál a dataSource már csak az aktuális oldal adatait tartalmazza,
    // nem szabad tovább slice-olni a paginator.pageIndex alapján
    if (this._serverSidePagination) {
      return this.dataSource.data;
    }
    
    const currentState = {
      pageIndex: paginator.pageIndex,
      pageSize: paginator.pageSize,
      filteredDataLength: this.dataSource.filteredData.length
    };
    
    if (this._lastPaginatorState && 
        this._lastPaginatorState.pageIndex === currentState.pageIndex &&
        this._lastPaginatorState.pageSize === currentState.pageSize &&
        this._lastPaginatorState.filteredDataLength === currentState.filteredDataLength) {
      return this._paginatedDataCache;
    }
    
    const startIndex = paginator.pageIndex * paginator.pageSize;
    const endIndex = startIndex + paginator.pageSize;
    this._paginatedDataCache = this.dataSource.filteredData.slice(startIndex, endIndex);
    this._lastPaginatorState = currentState;
    
    return this._paginatedDataCache;
  }

  /**
   * Paginator állapot frissítése
   */
  updatePaginatorState(pageIndex: number): void {
    if (!this.preservePaginatorState) {
      this.currentPageIndex = pageIndex;
    }
  }

  // === FILTER METHODS ===

  /**
   * Filter predicate létrehozása
   */
  createFilterPredicate(): (data: CallCenterDatabaseTableData, filter: string) => boolean {
    return (data: CallCenterDatabaseTableData, filter: string): boolean => {
      const searchText = this.searchTerm.toLowerCase();
      
      // Multi-select status szűrő
      const statusMatch = this.statusFilter.length === 0 || this.statusFilter.includes(data.status);
      
      // Hozzárendelés szűrő
      let assigneeMatch = true;
      
      if (this.authService.isCallCenterEmployee()) {
        assigneeMatch = true;
      } else if (this.assigneeFilter.length > 0) {
        const assignedEmployee = this.extractAssignedEmployee(data.notes);
        
        const hasUnassignedFilter = this.assigneeFilter.includes('unassigned');
        const specificStaffFilters = this.assigneeFilter.filter(f => f !== 'unassigned');
        
        let unassignedMatch = false;
        let staffMatch = false;
        
        if (hasUnassignedFilter) {
          unassignedMatch = assignedEmployee === null;
        }
        
        if (specificStaffFilters.length > 0 && assignedEmployee) {
          // Backend logika követése: COALESCE(NULLIF(u.username, ''), u.fullname)
          staffMatch = specificStaffFilters.some(filterValue => {
            return assignedEmployee === filterValue;
          });
        }
        
        assigneeMatch = unassignedMatch || staffMatch;
      }
      
      // Dátum intervallum szűrő
      let dateMatch = true;
      
      // Ha bármelyik dátum szűrő be van állítva, akkor a rekordnak kötelező dátummal rendelkeznie
      const hasDateFilter = this.dateFromFilter || this.dateToFilter;
      if (hasDateFilter && !data.date) {
        dateMatch = false;
      } else if (this.dateFromFilter && data.date) {
        dateMatch = dateMatch && data.date >= this.dateFromFilter;
      }
      
      if (this.dateToFilter && data.date && dateMatch) {
        const toDate = new Date(this.dateToFilter);
        toDate.setHours(23, 59, 59, 999);
        dateMatch = dateMatch && data.date <= toDate;
      }
      
      // Szöveges keresés több mezőben
      const emailsText = data.emails ? data.emails.join(', ').toLowerCase() : '';
      const textMatch = !searchText || 
        data.companyName.toLowerCase().includes(searchText) ||
        data.officials.toLowerCase().includes(searchText) ||
        data.phoneNumbers.toLowerCase().includes(searchText) ||
        emailsText.includes(searchText) ||
        data.notes.toLowerCase().includes(searchText) ||
        data.registrationNumber.toLowerCase().includes(searchText) ||
        data.county.toLowerCase().includes(searchText) ||
        data.postalCode.toLowerCase().includes(searchText);
      
      return statusMatch && assigneeMatch && dateMatch && textMatch;
    };
  }

  /**
   * Filter adatok alkalmazása wrapper komponensből
   */
  applyFilterData(filterData: FilterData): void {
    this.searchTerm = filterData.searchTerm;
    this.statusFilter = filterData.statusFilter;
    this.dateFromFilter = filterData.dateFromFilter;
    this.dateToFilter = filterData.dateToFilter;
    this.assigneeFilter = filterData.assigneeFilter;
  }

  /**
   * Összes szűrő törlése
   */
  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = [];
    this.assigneeFilter = [];
    this.dateFromFilter = null;
    this.dateToFilter = null;
  }

  /**
   * Jelenlegi szűrő állapot lekérése
   */
  getCurrentFilterState(): FilterData {
    return {
      searchTerm: this.searchTerm,
      statusFilter: this.statusFilter,
      dateFromFilter: this.dateFromFilter,
      dateToFilter: this.dateToFilter,
      selectedAssignee: null,
      assigneeFilter: this.assigneeFilter
    };
  }

  /**
   * Filter frissítés trigger és cache invalidálása
   */
  triggerFilterUpdate(): void {
    this.dataSource.filter = Math.random().toString();
    this._lastPaginatorState = null;
    this._paginatedDataCache = [];
  }

  // === SELECTION METHODS ===

  /**
   * Összes kijelölés törlése
   */
  clearSelection(): void {
    this.selection.clear();
  }

  /**
   * Minden látható elem ki van-e jelölve (csak az aktuális oldalon látható sorok)
   */
  isAllSelected(paginator?: MatPaginator | null, hidePaginator?: boolean): boolean {
    const visibleRows = this.getPaginatedData(paginator || null, hidePaginator || false);
    if (visibleRows.length === 0) return false;
    
    const numSelectedVisible = visibleRows.filter(row => this.selection.isSelected(row)).length;
    return numSelectedVisible === visibleRows.length;
  }

  /**
   * Látható sorok kijelölés toggle (csak az aktuális oldalon látható sorok)
   */
  toggleAllRows(paginator?: MatPaginator | null, hidePaginator?: boolean): void {
    const visibleRows = this.getPaginatedData(paginator || null, hidePaginator || false);
    
    if (this.isAllSelected(paginator, hidePaginator)) {
      // Ha minden látható sor ki van jelölve, akkor csak a láthatókat töröljük
      visibleRows.forEach(row => this.selection.deselect(row));
      return;
    }
    
    // Jelöljük ki a látható sorokat
    this.selection.select(...visibleRows);
  }

  /**
   * Checkbox label generálás
   */
  checkboxLabel(row?: CallCenterDatabaseTableData, paginator?: MatPaginator | null, hidePaginator?: boolean): string {
    if (!row) {
      return `${this.isAllSelected(paginator, hidePaginator) ? 'deselect' : 'select'} all visible`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id}`;
  }

  // === CACHE METHODS ===

  /**
   * Cache-elt status class lekérése vagy számítása
   */
  getCachedStatusClass(status: string, calculateFn: (status: string) => string): string {
    if (!status) return 'clickable-chip';
    
    if (this.statusClassCache.has(status)) {
      return this.statusClassCache.get(status)!;
    }
    
    const className = calculateFn(status);
    this.statusClassCache.set(status, className);
    
    return className;
  }

  /**
   * Status class cache törlése
   */
  clearStatusClassCache(): void {
    this.statusClassCache.clear();
  }

  /**
   * Minden cache törlése (status és pagination is)
   * Használható adatok frissítése után a UI teljes újrarajzolásához
   */
  clearAllCaches(): void {
    this.statusClassCache.clear();
    this._lastPaginatorState = null;
    this._paginatedDataCache = [];
  }

  // === CHIP STATUS INITIALIZATION ===

  /**
   * Chip státuszok inicializálása UI interakciókhoz
   */
  private initializeChipStatuses(data: CallCenterDatabaseTableData[]): void {
    data.forEach(item => {
      if (!item.officialStatuses) {
        item.officialStatuses = {};
        item.officials.split(', ').forEach(official => {
          item.officialStatuses![official.trim()] = 'default';
        });
      }
      if (!item.phoneStatuses) {
        item.phoneStatuses = {};
        item.phoneNumbers.split(', ').forEach(phone => {
          item.phoneStatuses![phone.trim()] = 'default';
        });
      }
      if (!item.emailStatuses) {
        item.emailStatuses = {};
        if (item.emails && item.emails.length > 0) {
          item.emails.forEach(email => {
            item.emailStatuses![email.trim()] = 'default';
          });
        }
      }
    });
  }

  // === UTILITY METHODS ===

  /**
   * Hozzárendelt munkatárs kinyerése a jegyzetekből
   */
  private extractAssignedEmployee(fullNotes: string): string | null {
    if (!fullNotes) return null;
    
    if (fullNotes.startsWith('assignment_ind ')) {
      const firstLine = fullNotes.split('\n')[0];
      const assignedEmployee = firstLine.substring('assignment_ind '.length).trim();
      return assignedEmployee;
    }
    
    if (fullNotes.startsWith('Hozzárendelve:')) {
      const firstLine = fullNotes.split('\n')[0];
      const assignedEmployee = firstLine.substring('Hozzárendelve:'.length).trim();
      return assignedEmployee;
    }
    return null;
  }
}